import React from 'react';
import './ResilienceMetrics.css';

function ResilienceMetrics({ resilience, velocity, comparative }) {
  if (!resilience && !velocity && !comparative) return null;

  return (
    <div className="resilience-metrics">
      <h3 className="resilience-metrics__title">Growth & Resilience</h3>

      {/* Resilience Score Card */}
      {resilience && (
        <section className="resilience-metrics__section">
          <div className="resilience-metrics__score-card">
            <div className="resilience-metrics__score-header">
              <h4>Resilience Score</h4>
              <div className="resilience-metrics__score-value">
                {resilience.resilienceScore || 0}<span>/10</span>
              </div>
            </div>
            
            <div className="resilience-metrics__score-bar">
              <div
                className="resilience-metrics__score-fill"
                style={{
                  width: `${((resilience.resilienceScore || 0) / 10) * 100}%`,
                  backgroundColor: (resilience.resilienceScore || 0) >= 7 ? '#10B981' : (resilience.resilienceScore || 0) >= 5 ? '#F59E0B' : '#EF4444'
                }}
              />
            </div>

            {resilience.recoveryTime && (
              <div className="resilience-metrics__recovery">
                <div className="resilience-metrics__recovery-icon">
                  {resilience.recoveryTime.improving ? '📈' : '📊'}
                </div>
                <div className="resilience-metrics__recovery-text">
                  <div className="resilience-metrics__recovery-value">
                    {resilience.recoveryTime.average?.toFixed(1) || 0} days
                  </div>
                  <div className="resilience-metrics__recovery-label">
                    Average recovery time
                    {resilience.recoveryTime.improving && resilience.recoveryTime.previousPeriod && (
                      <span className="resilience-metrics__improvement">
                        ↓ from {resilience.recoveryTime.previousPeriod.toFixed(1)} days
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Emotional Stability */}
      {velocity && (
        <section className="resilience-metrics__section">
          <h4 className="resilience-metrics__subtitle">Emotional Stability</h4>
          <div className="resilience-metrics__stability-grid">
            <div className="resilience-metrics__stability-card">
              <div className="resilience-metrics__stability-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                </svg>
              </div>
              <div className="resilience-metrics__stability-value">
                {velocity.stabilityScore || 0}/10
              </div>
              <div className="resilience-metrics__stability-label">Stability Score</div>
            </div>

            <div className="resilience-metrics__stability-card">
              <div className="resilience-metrics__stability-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18"/>
                  <polyline points="9 17 9 10 13 6 17 10 17 17"/>
                </svg>
              </div>
              <div className="resilience-metrics__stability-value">
                {velocity.rapidShifts || 0}
              </div>
              <div className="resilience-metrics__stability-label">Rapid Mood Shifts</div>
            </div>

          <div className="resilience-metrics__stability-card">
            <div className="resilience-metrics__stability-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
            </div>
            <div className="resilience-metrics__stability-value">
              {velocity.averageShiftMagnitude?.toFixed(1) || 0}
            </div>
            <div className="resilience-metrics__stability-label">Avg Shift Size</div>
          </div>
        </div>

        <div className="resilience-metrics__volatility">
          <div className="resilience-metrics__volatility-label">Volatility Index:</div>
          <div className="resilience-metrics__volatility-bar">
            <div
              className="resilience-metrics__volatility-fill"
              style={{
                width: `${(velocity.volatilityIndex || 0) * 100}%`,
                backgroundColor: (velocity.volatilityIndex || 0) < 0.3 ? '#10B981' : (velocity.volatilityIndex || 0) < 0.6 ? '#F59E0B' : '#EF4444'
              }}
            />
          </div>
          <div className="resilience-metrics__volatility-value">
            {((velocity.volatilityIndex || 0) * 100).toFixed(0)}%
            <span className="resilience-metrics__volatility-note">
              (lower is more stable)
            </span>
          </div>
        </div>

        {velocity.interpretation && (
          <div className="resilience-metrics__interpretation">
            💡 {velocity.interpretation}
          </div>
        )}
      </section>
      )}

      {/* Coping Strategies */}
      {resilience?.copingStrategies && resilience.copingStrategies.length > 0 && (
      <section className="resilience-metrics__section">
        <h4 className="resilience-metrics__subtitle">Effective Coping Strategies</h4>
        <div className="resilience-metrics__coping-list">
          {resilience.copingStrategies
            .sort((a, b) => b.effectiveness - a.effectiveness)
            .map((strategy, index) => (
              <div key={index} className="resilience-metrics__coping-card">
                <div className="resilience-metrics__coping-header">
                  <h5 className="resilience-metrics__coping-name">{strategy.strategy}</h5>
                  <div className="resilience-metrics__coping-effectiveness">
                    <div className="resilience-metrics__effectiveness-bar">
                      <div
                        className="resilience-metrics__effectiveness-fill"
                        style={{ width: `${strategy.effectiveness * 100}%` }}
                      />
                    </div>
                    <span className="resilience-metrics__effectiveness-value">
                      {(strategy.effectiveness * 100).toFixed(0)}% effective
                    </span>
                  </div>
                </div>
                <div className="resilience-metrics__coping-stats">
                  <div className="resilience-metrics__coping-stat">
                    <span className="resilience-metrics__coping-stat-label">Used:</span>
                    <span className="resilience-metrics__coping-stat-value">
                      {strategy.timesUsed} times
                    </span>
                  </div>
                  <div className="resilience-metrics__coping-stat">
                    <span className="resilience-metrics__coping-stat-label">Recovery:</span>
                    <span className="resilience-metrics__coping-stat-value">
                      {strategy.averageRecoveryTime.toFixed(1)} days
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </section>
      )}

      {/* Emotional Growth */}
      {resilience && (resilience.emotionalVocabulary || resilience.selfAwarenessIndicators) && (
      <section className="resilience-metrics__section">
        <h4 className="resilience-metrics__subtitle">Emotional Growth Indicators</h4>
        <div className="resilience-metrics__growth-grid">
          {resilience.emotionalVocabulary && (
            <div className="resilience-metrics__growth-card">
              <div className="resilience-metrics__growth-value">
                {resilience.emotionalVocabulary.uniqueEmotionWords || 0}
              </div>
              <div className="resilience-metrics__growth-label">
                Unique emotion words
              </div>
              {resilience.emotionalVocabulary.growth > 0 && (
                <div className="resilience-metrics__growth-badge">
                  +{resilience.emotionalVocabulary.growth} this period
                </div>
              )}
            </div>
          )}

          {resilience.selfAwarenessIndicators && (
            <div className="resilience-metrics__growth-card">
              <div className="resilience-metrics__growth-value">
                {resilience.selfAwarenessIndicators.reflectiveEntries || 0}
              </div>
              <div className="resilience-metrics__growth-label">
                Reflective entries ({resilience.selfAwarenessIndicators.percentage?.toFixed(1) || 0}%)
              </div>
              {resilience.selfAwarenessIndicators.trend && (
                <div className="resilience-metrics__growth-trend">
                  {resilience.selfAwarenessIndicators.trend}
                </div>
              )}
            </div>
          )}
        </div>

        {resilience.selfAwarenessIndicators?.insight && (
          <div className="resilience-metrics__growth-insight">
            {resilience.selfAwarenessIndicators.insight}
          </div>
        )}
      </section>
      )}

      {/* Comparative Insights */}
      {comparative?.vsLastPeriod && (
        <section className="resilience-metrics__section">
          <h4 className="resilience-metrics__subtitle">Your Progress</h4>
          
          <div className="resilience-metrics__comparison">
            <div className="resilience-metrics__comparison-header">
              <span className="resilience-metrics__comparison-label">
                vs. {comparative.vsLastPeriod.periodName || 'Previous Period'}
              </span>
              <span className={`resilience-metrics__comparison-change resilience-metrics__comparison-change--${comparative.vsLastPeriod.direction || 'stable'}`}>
                {(comparative.vsLastPeriod.moodScoreChange || 0) > 0 ? '+' : ''}
                {(comparative.vsLastPeriod.moodScoreChange || 0).toFixed(1)}
                ({(comparative.vsLastPeriod.percentageChange || 0) > 0 ? '+' : ''}
                {(comparative.vsLastPeriod.percentageChange || 0).toFixed(1)}%)
              </span>
            </div>
            
            {comparative.vsLastPeriod.keyChanges && comparative.vsLastPeriod.keyChanges.length > 0 && (
              <ul className="resilience-metrics__key-changes">
                {comparative.vsLastPeriod.keyChanges.map((change, index) => (
                  <li key={index} className="resilience-metrics__key-change">
                    {change}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {comparative.bestPeriodEver && (
            <div className="resilience-metrics__best-period">
              <div className="resilience-metrics__best-period-header">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
                <h5>Your Best Period Ever</h5>
              </div>
              <div className="resilience-metrics__best-period-info">
                <div className="resilience-metrics__best-period-name">
                  {comparative.bestPeriodEver.period}
                </div>
                <div className="resilience-metrics__best-period-score">
                  Average score: {comparative.bestPeriodEver.averageScore?.toFixed(1) || 0}
                </div>
              </div>
              {comparative.bestPeriodEver.whatWasDifferent && comparative.bestPeriodEver.whatWasDifferent.length > 0 && (
                <div className="resilience-metrics__best-period-factors">
                  <div className="resilience-metrics__best-period-label">What made it great:</div>
                  <ul>
                    {comparative.bestPeriodEver.whatWasDifferent.map((factor, index) => (
                      <li key={index}>{factor}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default ResilienceMetrics;
